/**
 * Утилиты для работы с номерами автомобилей
 */

/**
 * Форматирует номер автомобиля в российском формате с пробелами для отображения: А 123 БВ 77
 * @param value - введенная строка
 * @returns отформатированный номер автомобиля с пробелами
 */
export const formatCarNumber = (value: string): string => {
  // Убираем все нецифровые и небуквенные символы
  const cleaned = value.replace(/[^А-Яа-яA-Za-z0-9]/g, '').toUpperCase();

  // Применяем маску: А 123 БВ 77 (1 буква + пробел + 3 цифры + пробел + 2 буквы + пробел + до 3 цифр)
  let formatted = '';

  // Первая буква
  if (cleaned.length > 0) {
    formatted += cleaned[0];
  }

  // Пробел и три цифры
  if (cleaned.length > 1) {
    formatted += ' ';
    const digits = cleaned.substring(1, 4).replace(/[^0-9]/g, '');
    formatted += digits;
  }

  // Пробел и две буквы
  if (cleaned.length > 4) {
    formatted += ' ';
    const letters = cleaned.substring(4, 6).replace(/[^А-Яа-яA-Za-z]/g, '');
    formatted += letters;
  }

  // Пробел и регион (до 3 цифр)
  if (cleaned.length > 6) {
    formatted += ' ';
    const region = cleaned.substring(6, 9).replace(/[^0-9]/g, '');
    formatted += region;
  }

  return formatted;
};

/**
 * Форматирует номер автомобиля для отправки на бэкенд (без пробелов): А123БВ77
 * @param value - номер с пробелами или без
 * @returns номер без пробелов для бэкенда
 */
export const formatCarNumberForBackend = (value: string): string => {
  // Убираем все нецифровые и небуквенные символы (включая пробелы)
  const cleaned = value.replace(/[^А-Яа-яA-Za-z0-9]/g, '').toUpperCase();

  // Применяем маску без пробелов: А123БВ77
  let formatted = '';

  // Первая буква
  if (cleaned.length > 0) {
    formatted += cleaned[0];
  }

  // Три цифры
  if (cleaned.length > 1) {
    const digits = cleaned.substring(1, 4).replace(/[^0-9]/g, '');
    formatted += digits;
  }

  // Две буквы
  if (cleaned.length > 4) {
    const letters = cleaned.substring(4, 6).replace(/[^А-Яа-яA-Za-z]/g, '');
    formatted += letters;
  }

  // Регион (до 3 цифр)
  if (cleaned.length > 6) {
    const region = cleaned.substring(6, 9).replace(/[^0-9]/g, '');
    formatted += region;
  }

  return formatted;
};

/**
 * Валидирует номер автомобиля в российском формате
 * @param number - номер автомобиля (с пробелами или без)
 * @returns true если номер корректный
 */
export const validateCarNumber = (number: string): boolean => {
  // Сначала очищаем от пробелов
  const cleanNumber = formatCarNumberForBackend(number);
  // Регулярное выражение для российского номера: 1 буква + 3 цифры + 2 буквы + 2-3 цифры
  const carNumberRegex = /^[А-Я]{1}[0-9]{3}[А-Я]{2}[0-9]{2,3}$/;
  return carNumberRegex.test(cleanNumber);
};

/**
 * Очищает номер автомобиля от лишних символов
 * @param number - номер автомобиля
 * @returns очищенный номер
 */
export const cleanCarNumber = (number: string): string => {
  return number.replace(/[^А-Яа-яA-Za-z0-9]/g, '').toUpperCase();
};
